import React, { useEffect, useState, useRef, useCallback } from "react";
import { addAdmission, deleteAdmission, approveAdmission } from "../../api/api";
import { useGlobalData } from "../../context/GlobalDataContext";
import "../superadmin/AdminPanel.css";

const createInitialForm = () => ({
  title: "",
  slug: ""
});

function AdministrativeAdmissionAdminPanel({ darkMode }) {
  const { threeTable,fetchThreeTableSection, threeTableHasMore } = useGlobalData();
  const [isFetchingMore, setIsFetchingMore] = useState(false);
  const listRef = useRef(null);
  const PAGE_LIMIT = 29;
  const [formData, setFormData] = useState(createInitialForm());
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const role = localStorage.getItem("role");

  // Initial load (global state)
  useEffect(() => {
    let ignore = false;
    async function fetchInitial() {
      setIsLoading(true);
      try {
        await fetchThreeTableSection("admissions", false, { limit: PAGE_LIMIT, offset: 0 });
      } finally {
        if (!ignore) setIsLoading(false);
      }
    }
    fetchInitial();
    return () => { ignore = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Fetch more admissions (global state)
  const fetchMoreAdmissions = useCallback(async () => {
    setIsFetchingMore(true);
    try {
      await fetchThreeTableSection("admissions", false, { limit: PAGE_LIMIT, offset: threeTable.admissions.length });
    } finally {
      setIsFetchingMore(false);
    }
  }, [fetchThreeTableSection, threeTable.admissions.length]);

  // Infinite scroll handler
  const handleScroll = useCallback(() => {
    if (!threeTableHasMore.admissions || isFetchingMore) return;
    const el = listRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    if (rect.bottom <= window.innerHeight + 200) {
      fetchMoreAdmissions();
    }
  }, [threeTableHasMore.admissions, isFetchingMore, fetchMoreAdmissions]);

  // Attach scroll event
  useEffect(() => {
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, [handleScroll]);

  const handleSubmit = async (event) => {
    event.preventDefault();
    setMessage("");
    setError("");
    try {
      const title = formData.title.trim();
      const slug = formData.slug.trim();
      if (!title) {
        throw new Error("Title required hai.");
      }
      if (!slug) {
        throw new Error("Slug required hai.");
      }
      await addAdmission({ title, slug });
      setMessage("Admission added!");
      setFormData(createInitialForm());
      // No need to refresh global list; admissions are managed locally now
    } catch (err) {
      setError(err.message || "Unable to add admission");
    }
  };

  const handleApprove = async (id) => {
    try {
      await approveAdmission(id);
      setMessage("Admission approved!");
      // No need to refresh global list; admissions are managed locally now
    } catch (err) {
      setError(err.message || "Unable to approve admission");
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm("Delete this admission?")) {
      try {
        await deleteAdmission(id);
        setMessage("Admission deleted!");
        // No need to refresh global list; admissions are managed locally now
      } catch (err) {
        setError(err.message || "Unable to delete admission");
      }
    }
  };

  if (role !== "administrative") {
    return <div className="admin-panel-error">❌ Access denied! Only Administrative users can access this panel.</div>;
  }

  return (
    <div className={`resource-admin-panel resource-admin-panel--purple${darkMode ? " dark-mode" : ""}`}>
      <div className="resource-admin-panel__header">
        <div>
          <h2 className="resource-admin-panel__heading">Administrative Admission Admin Panel</h2>
          <p className="resource-admin-panel__subtext">Yahan se admission records ko add, review aur delete kar sakte hain. Title aur slug required hai.</p>
        </div>
        <div className="resource-admin-panel__pill">Admission</div>
      </div>
      <form className="resource-admin-panel__form" onSubmit={handleSubmit}>
        <div className="resource-admin-panel__field">
          <label>Title:</label>
          <input
            type="text"
            value={formData.title}
            onChange={(e) => setFormData({ ...formData, title: e.target.value })}
            required
          />
        </div>
        <div className="resource-admin-panel__field">
          <label>Slug:</label>
          <input
            type="text"
            value={formData.slug}
            onChange={(e) => setFormData({ ...formData, slug: e.target.value })}
            required
          />
        </div>
        <button type="submit">Add Admission</button>
      </form>
      {message && <div className="success-message">{message}</div>}
      {error && <div className="error-message">{error}</div>}
      <div className="resource-admin-panel__list" ref={listRef} style={{ maxHeight: "800px", overflowY: "auto" }}>
        <h3>Admission Entries</h3>
        {isLoading ? (
          <div>Loading...</div>
        ) : (
          <ul>
            {threeTable.admissions.map((admission) => {
              const isPending = admission.status === "pending";
              const statusColor = isPending ? "#f7b500" : "#2ecc40";
              return (
                <li key={admission._id} style={{ display: "flex", alignItems: "center", gap: 12 }}>
                  <span>{admission.title} ({admission.slug})</span>
                  <span style={{
                    background: statusColor,
                    color: "#fff",
                    borderRadius: 6,
                    padding: "2px 10px",
                    fontWeight: 600,
                    fontSize: 13
                  }}>{admission.status === "pending" ? "Pending" : "Approved"}</span>
                  {isPending && (
                    <>
                      <button onClick={() => handleApprove(admission._id)} style={{ background: "#f7b500", color: "#fff", marginLeft: 8 }}>Approve</button>
                      <button onClick={() => handleDelete(admission._id)} style={{ marginLeft: 8 }}>Delete</button>
                    </>
                  )}
                  {!isPending && null}
                </li>
              );
            })}
          </ul>
        )}
        {isFetchingMore && <div style={{ textAlign: "center", margin: 10 }}>Loading more...</div>}
        {!threeTableHasMore.admissions && !isLoading && threeTable.admissions.length > 0 && <div style={{ textAlign: "center", margin: 10, color: "#888" }}>No more admissions.</div>}
      </div>
    </div>
  );
}

export default AdministrativeAdmissionAdminPanel;
